/**
 * Shared collage persistence helpers.
 *
 * Used by:
 *   - HomePage's `handleSaveClick` (the canonical SAVE flow when finishing the inline survey
 *     or editing an existing collage).
 *   - FoxChat's `handleSaveCollage` (the new chat-driven SAVE flow that mirrors HomePage's
 *     private save — writes to users/{uid}.savedWantedPosters but never publishes to feed).
 *
 * Behavior here MUST stay byte-compatible with what HomePage's editor expects to hydrate
 * from the saved doc (see HomePage's collage rendering + `posterData` shape).
 */

import { storage } from '../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { parseAuraRgb } from './auraColor';

/**
 * Persist an image URL (data:, replicate.delivery, relative path, or any http(s)) to Firebase
 * Storage and return the resulting downloadURL. Stable http(s) URLs on non-replicate origins
 * are returned as-is (no re-upload).
 *
 * Returns null when a data:/replicate URL fails to upload, or the original URL when a relative
 * URL fails to fetch.
 */
export async function persistImageUrlToStorage(url, folder, defaultExt = 'png') {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:')) {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = (blob.type && blob.type.split('/')[1]) || defaultExt;
      const path = `${folder}/${Date.now()}_${nanoid(8)}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob, { contentType: blob.type || `image/${defaultExt}` });
      return await getDownloadURL(ref);
    } catch (e) {
      console.warn('Could not persist data URL to Storage:', e);
      return null;
    }
  }
  if (url.includes('replicate.delivery')) {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      const ext = (blob.type && blob.type.split('/')[1]) || defaultExt;
      const path = `${folder}/${Date.now()}_${nanoid(8)}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob, { contentType: blob.type || `image/${defaultExt}` });
      return await getDownloadURL(ref);
    } catch (e) {
      console.warn('Could not persist Replicate URL to Storage:', e);
      return null;
    }
  }
  if (url.startsWith('/') || !url.startsWith('http')) {
    try {
      const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      const res = await fetch(fullUrl);
      if (!res.ok) return url;
      const blob = await res.blob();
      const ext = (blob.type && blob.type.split('/')[1]) || defaultExt;
      const path = `${folder}/${Date.now()}_${nanoid(8)}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob, { contentType: blob.type || `image/${defaultExt}` });
      return await getDownloadURL(ref);
    } catch (e) {
      console.warn('Could not persist image to Storage, using original URL:', e);
      return url;
    }
  }
  return url;
}

/** Fetch an external https image and upload a copy to Firebase Storage; returns download URL or null. */
export async function persistHttpsImageToStorage(url, folder, defaultExt = 'jpg') {
  if (!url || typeof url !== 'string' || !url.startsWith('http')) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const ext = (blob.type && blob.type.split('/')[1]) || defaultExt;
    const path = `${folder}/${Date.now()}_${nanoid(8)}.${ext}`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, blob, { contentType: blob.type || `image/${defaultExt}` });
    return await getDownloadURL(ref);
  } catch (e) {
    console.warn('Could not persist https image to Storage:', e);
    return null;
  }
}

/**
 * Concurrently persist a collage's hero images (partner cutout + masked + unmasked user photo)
 * and the per-tile arrays (swags + places).
 *
 * Returns the same shape HomePage's `handleSaveClick` consumes — caller can spread the result
 * straight into the `posterData` object.
 */
export async function persistCollageImageBundle({
  partnerCutout = null,
  userPhotoMasked = null,
  userPhotoOriginal = null,
  swags = [],
  places = [],
  /** Optional fallbacks: when the masked/original user photos are missing, fall back to the
   *  user's profile photo (used by HomePage when the user has a stored profile photo but no
   *  fresh upload in the current session). FoxChat doesn't pass these. */
  profilePhotoMaskedFallback = null,
  profilePhotoOriginalFallback = null,
} = {}) {
  const clip = (u) => (u && typeof u === 'string' && u.trim() ? u.trim() : null);
  const [partnerCutoutUrl, userPhotoUrlForStore, userPhotoOriginalUrlForStore, swagsForStore, placesForStore] = await Promise.all([
    (async () => {
      if (!partnerCutout || typeof partnerCutout !== 'string') return partnerCutout || null;
      const u = await persistImageUrlToStorage(partnerCutout, 'partner_cutouts/generated', 'png');
      return u || (partnerCutout.startsWith('data:') || partnerCutout.includes('replicate.delivery') ? null : partnerCutout);
    })(),
    (async () => {
      if (userPhotoMasked && typeof userPhotoMasked === 'string') {
        const u = await persistImageUrlToStorage(userPhotoMasked, 'user_photos', 'jpg');
        return u || (userPhotoMasked.startsWith('data:') ? null : userPhotoMasked);
      }
      if (profilePhotoMaskedFallback && typeof profilePhotoMaskedFallback === 'string') {
        return (await persistImageUrlToStorage(profilePhotoMaskedFallback, 'user_photos', 'jpg')) || null;
      }
      return null;
    })(),
    (async () => {
      const raw = clip(userPhotoOriginal) || clip(profilePhotoOriginalFallback);
      if (!raw) return null;
      const u = await persistImageUrlToStorage(raw, 'user_photos', 'jpg');
      return u || (raw.startsWith('data:') ? null : raw);
    })(),
    Promise.all(
      (swags || []).map(async (swag) => {
        const rawUrl = swag?.imageUrl || (typeof swag === 'string' ? swag : null);
        const stableUrl = rawUrl && typeof rawUrl === 'string'
          ? await persistImageUrlToStorage(rawUrl, 'swags', 'png')
          : null;
        return {
          imageUrl: stableUrl || (rawUrl && typeof rawUrl === 'string' && !rawUrl.startsWith('data:') ? rawUrl : null),
          name: swag?.name || null,
          id: swag?.id || null,
        };
      }),
    ),
    Promise.all(
      (places || []).map(async (place) => {
        const rawUrl = place?.imageUrl;
        const stableUrl = rawUrl && typeof rawUrl === 'string'
          ? await persistImageUrlToStorage(rawUrl, 'places', 'jpg')
          : null;
        return {
          imageUrl: stableUrl || (rawUrl && typeof rawUrl === 'string' && !rawUrl.startsWith('data:') ? rawUrl : null),
          name: place?.name ?? null,
          id: place?.id ?? null,
        };
      }),
    ),
  ]);

  return { partnerCutoutUrl, userPhotoUrlForStore, userPhotoOriginalUrlForStore, swagsForStore, placesForStore };
}

const VALID_GENDER_IDS = new Set(['male', 'female', 'nb']);

/**
 * Build the canonical poster doc that gets stored inside `users/{uid}.savedWantedPosters`.
 * Both HomePage's new-collage SAVE and FoxChat's MVP SAVE produce this exact shape — editing
 * flows in HomePage layer their own merge logic on top of this base.
 *
 * `selfAge` and `lookingForGenders` are only included when present (HomePage doesn't collect
 * them at SAVE time today; FoxChat does).
 */
export function buildPosterDoc({
  id,
  createdAt = new Date(),
  userId = null,
  surveyAnswers = null,
  imageBundle,
  posterNickname,
  selfGender = null,
  friendLuv = false,
  romanticLuv = false,
  lookingForGenders = null,
  selfAge = null,
  homeBase = null,
  words = [],
  isPrivateCollage = false,
  postedIrl = false,
  postedTiktok = false,
  friendCollageShare = false,
  friendFoxMaskPlaceholder = false,
  cutoutCustomizedSinceSurveyDefault,
} = {}) {
  if (!imageBundle) {
    throw new Error('buildPosterDoc requires imageBundle');
  }
  const acts = Array.isArray(surveyAnswers?.activities) ? surveyAnswers.activities : [];
  const act0 = acts[0];
  const act1 = acts[1];
  const act2 = acts[2];
  const activityX = act0 === undefined ? 'Seeking' : typeof act0 === 'string' ? act0 : (act0.name || act0.emoji || 'Seeking');
  const activityY = act1 === undefined ? 'Match' : typeof act1 === 'string' ? act1 : (act1.name || act1.emoji || 'Match');
  const activityZ = act2 === undefined ? null : typeof act2 === 'string' ? act2 : (act2.name || act2.emoji || null);

  const heartPhotoUrl = surveyAnswers?.vibe_photo || null;
  const cutoutCustomized = typeof cutoutCustomizedSinceSurveyDefault === 'boolean'
    ? cutoutCustomizedSinceSurveyDefault
    : !!surveyAnswers?.user_photo;

  const out = {
    id,
    createdAt,
    userId: userId || null,
    surveyAnswers: surveyAnswers || null,
    partnerCutout: imageBundle.partnerCutoutUrl || null,
    heartPhotoUrl,
    swags: imageBundle.swagsForStore || [],
    places: imageBundle.placesForStore || [],
    activityX,
    activityY,
    activityZ: activityZ || null,
    words: Array.isArray(words) ? words : [],
    userPhotoUrl: imageBundle.userPhotoUrlForStore || null,
    userPhotoOriginalUrl: imageBundle.userPhotoOriginalUrlForStore || null,
    posterNickname,
    isPrivateCollage: !!isPrivateCollage,
    postedIrl: !!postedIrl,
    postedTiktok: !!postedTiktok,
    friendLuv: !!friendLuv,
    romanticLuv: !!romanticLuv,
    friendCollageShare: !!friendCollageShare,
    friendFoxMaskPlaceholder: !!friendFoxMaskPlaceholder,
    cutoutCustomizedSinceSurveyDefault: cutoutCustomized,
  };
  if (selfGender && VALID_GENDER_IDS.has(selfGender)) {
    out.selfGender = selfGender;
  }
  if (Array.isArray(lookingForGenders) && lookingForGenders.length) {
    out.lookingForGenders = [...lookingForGenders];
  }
  if (typeof selfAge === 'number' && Number.isFinite(selfAge)) {
    out.selfAge = selfAge;
  }
  if (typeof homeBase === 'string' && homeBase.trim()) {
    out.homeBase = homeBase.trim();
  }
  return out;
}

/**
 * Coerce undefined → null and recursively drop undefined object props so the value is safe to
 * pass to Firestore `setDoc`/`updateDoc`. Mirrors HomePage.js' inline `sanitizeForFirestore`.
 */
export function sanitizeForFirestore(val) {
  if (val === undefined) return null;
  if (val === null || typeof val !== 'object') return val;
  if (val instanceof Date || (typeof val.toDate === 'function')) return val;
  if (Array.isArray(val)) return val.map(sanitizeForFirestore).filter((v) => v !== undefined);
  const out = {};
  for (const [k, v] of Object.entries(val)) {
    if (v === undefined) continue;
    const s = sanitizeForFirestore(v);
    if (s !== undefined) out[k] = s;
  }
  return out;
}

/**
 * `sharedPosters/*` is world-readable (powers `/poster/{id}` shareable links). Strip the unmasked
 * user photo before mirroring there. Mirrors HomePage.js' inline `stripUnmaskedPhotoForPublicShare`.
 */
export function stripUnmaskedPhotoForPublicShare(poster) {
  if (!poster || typeof poster !== 'object') return poster;
  const { userPhotoOriginalUrl: _omit, ...rest } = poster;
  return rest;
}

/**
 * Mirror a saved poster to `sharedPosters/{id}` so it's reachable via the public share URL.
 *
 * This is the A.2 / B.4 write described in the SAVE → SAVED! → MEET PEOPLE flow:
 *   - HomePage's `handleSaveClick` writes this immediately after persisting `savedWantedPosters`.
 *   - HomePage's `handleSavedPosterLookingForConfirm` then merges `lookingForGenders` separately
 *     after the SAVED popup. FoxChat folds those in upfront because it collects
 *     `lookingForGenders` mid-chat, so a single mirror write at SAVE covers both A.2 + B.4.
 *
 * Always strips the unmasked user photo (sharedPosters is public) and coerces `createdAt` to a
 * Date when missing. Sanitize-for-firestore drops undefined fields.
 *
 * Also mirrors the owner's permanent `auraColor` (from `users/{uid}` or `options.auraColor`)
 * so public nickname tiles on share links / weekly featured can render the vibe aura.
 */
export async function writeSharedPosterMirror(firestore, poster, options = {}) {
  if (!firestore || !poster?.id) return;
  const raw = { ...poster };
  if (raw.createdAt && typeof raw.createdAt.toDate === 'function') {
    /* Firestore Timestamp — leave as-is for setDoc. */
  } else if (!(raw.createdAt instanceof Date)) {
    raw.createdAt = new Date();
  }
  let aura = parseAuraRgb(options.auraColor) || parseAuraRgb(raw.auraColor);
  if (!aura && poster.userId) {
    try {
      const userSnap = await getDoc(doc(firestore, 'users', poster.userId));
      aura = parseAuraRgb(userSnap.data()?.auraColor);
    } catch (_) {
      /* owner-only read; ignore when unavailable */
    }
  }
  if (aura) raw.auraColor = aura;
  await setDoc(
    doc(firestore, 'sharedPosters', poster.id),
    sanitizeForFirestore(stripUnmaskedPhotoForPublicShare(raw)),
  );
}
